import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send, MessagesSquare, Users, User as UserIcon } from "lucide-react";
import VipBadge from "@/components/app/VipBadge";

const POLL_INTERVAL_MS = 3000;

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(0);
  const listRef = useRef(null);
  const lastTsRef = useRef(null);
  const stickBottomRef = useRef(true);

  const bottomize = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // initial load
    api.get("/chat/messages", { params: { limit: 100 } }).then((r) => {
      setMessages(r.data);
      if (r.data.length) lastTsRef.current = r.data[r.data.length - 1].created_at;
      requestAnimationFrame(bottomize);
    });
    api.get("/chat/online").then((r) => setOnline(r.data.online_last_5m || 0));
  }, []);

  useEffect(() => {
    const int = setInterval(async () => {
      try {
        if (!lastTsRef.current) return;
        const { data } = await api.get(`/chat/messages/since/${encodeURIComponent(lastTsRef.current)}`);
        if (data.length) {
          setMessages((prev) => [...prev, ...data]);
          lastTsRef.current = data[data.length - 1].created_at;
          if (stickBottomRef.current) requestAnimationFrame(bottomize);
        }
      } catch {}
    }, POLL_INTERVAL_MS);
    const onlineInt = setInterval(() => {
      api.get("/chat/online").then((r) => setOnline(r.data.online_last_5m || 0)).catch(() => {});
    }, 15000);
    return () => {
      clearInterval(int);
      clearInterval(onlineInt);
    };
  }, []);

  const onScroll = () => {
    if (!listRef.current) return;
    const el = listRef.current;
    stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const send = async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { content });
      setMessages((prev) => [...prev, data]);
      lastTsRef.current = data.created_at;
      setInput("");
      requestAnimationFrame(bottomize);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-10 relative z-10">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="tape-label">GLOBAL CHATROOM</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            THE<br /><span className="text-[var(--primary)] glow">TERMINAL.</span>
          </h1>
          <p className="text-[var(--text-muted)] mt-4 max-w-md">
            Real-time. All ascending brothers. Keep it useful — mods watch.
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 text-xs font-mono text-[var(--primary)]">
            <Users className="w-3.5 h-3.5" />
            <span data-testid="chat-online-count">{online} active · last 5m</span>
          </div>
        </div>
      </div>

      <div className="panel flex flex-col h-[75vh]">
        <div
          ref={listRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
          data-testid="chat-messages"
        >
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <MessagesSquare className="w-10 h-10 text-[var(--primary)] mb-3" />
              <div className="font-display text-3xl">SILENCE.</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-2">
                Be the first to break it.
              </div>
            </div>
          )}
          {messages.map((m) => (
            <MessageRow key={m.id} m={m} isMe={m.user_id === user?.id} />
          ))}
        </div>

        <form
          onSubmit={send}
          className="border-t border-[var(--border)] p-3 flex items-end gap-2 bg-[var(--surface)]"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={500}
            placeholder="Type… enter to send"
            className="brutal"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="btn-brutal px-4"
            data-testid="chat-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function MessageRow({ m, isMe }) {
  const isSystem = m.is_system || m.user_id === "system";
  if (isSystem) {
    return (
      <div className="text-center" data-testid="chat-message-system">
        <div className="inline-block px-4 py-2 border border-[var(--primary)] bg-[var(--primary-soft)] text-[var(--primary)] font-mono text-xs uppercase tracking-widest">
          <span className="opacity-70">[SYSTEM]</span> {m.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3" data-testid={`chat-message-${m.id}`}>
      <div className="w-8 h-8 border border-[var(--border-bright)] bg-[var(--surface-2)] flex items-center justify-center overflow-hidden shrink-0">
        {m.avatar_base64 ? (
          <img src={`data:image/jpeg;base64,${m.avatar_base64}`} alt={m.username} className="w-full h-full object-cover" />
        ) : (
          <UserIcon className="w-4 h-4 text-[var(--text-muted)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className={isMe ? "text-[var(--primary)] font-bold" : "text-white"}>
            @{m.username}
          </span>
          <VipBadge user={m} size="sm" />
          <span className="text-[10px] text-[var(--text-muted)]">
            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div className="text-sm text-[var(--text)] mt-0.5 break-words whitespace-pre-wrap">{m.content}</div>
      </div>
    </div>
  );
}
